<template>
  <section
    v-if="reviews.length > 0"
    id="reviews"
    class="bg-blueberry-lighter/15 py-10 md:py-16"
  >
    <div class="container mx-auto px-4">
      <div class="max-w-3xl mx-auto mb-8 md:mb-12 text-center">
        <h2 class="text-3xl md:text-4xl font-bold text-navy mb-6">
          {{ t('reviews.title') }}
        </h2>
        <div class="w-24 h-1 bg-leaf mx-auto mb-6 rounded-full"></div>
        <p v-if="rating !== null" class="text-muted-foreground">
          {{ t('reviews.summary', { rating: formattedRating, count: userRatingCount }) }}
        </p>
      </div>

      <div
        class="relative max-w-3xl mx-auto"
        @mouseenter="pause"
        @mouseleave="resume"
      >
        <div class="overflow-hidden">
          <div
            class="flex transition-transform duration-500 ease-out"
            :style="{ transform: `translateX(-${currentIndex * 100}%)` }"
          >
            <div
              v-for="review in reviews"
              :key="review.id"
              class="min-w-full self-stretch px-2"
            >
              <Card class="h-full">
                <CardContent class="flex h-full flex-col pt-6">
                  <div class="flex items-center gap-1 text-amber-400" aria-hidden="true">
                    <Star
                      v-for="star in 5"
                      :key="star"
                      class="w-5 h-5"
                      :class="star <= Math.round(review.rating) ? 'fill-current' : 'text-muted-foreground/30'"
                    />
                  </div>
                  <blockquote class="mt-4 flex-1 italic text-foreground">
                    “{{ review.text }}”
                  </blockquote>
                  <div class="mt-4 flex items-center gap-3">
                    <div
                      class="flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-md bg-white text-muted-foreground"
                    >
                      <img
                        v-if="review.profile_photo_url"
                        :src="review.profile_photo_url"
                        :alt="review.author_name"
                        width="40"
                        height="40"
                        loading="lazy"
                        class="h-full w-full object-contain"
                      />
                      <UserRound v-else class="h-5 w-5" aria-hidden="true" />
                    </div>
                    <div class="text-left">
                      <p class="font-semibold text-navy">{{ review.author_name }}</p>
                      <p class="min-h-5 text-sm text-muted-foreground">
                        {{ review.relative_time }}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>

        <template v-if="reviews.length > 1">
          <button
            type="button"
            class="absolute top-1/2 -translate-y-1/2 -left-3 md:-left-5 bg-background shadow-md rounded-full p-2 text-navy hover:bg-blueberry-lighter/30 transition-colors"
            :aria-label="t('reviews.previous')"
            @click="prev"
          >
            <ChevronLeft class="w-5 h-5" />
          </button>
          <button
            type="button"
            class="absolute top-1/2 -translate-y-1/2 -right-3 md:-right-5 bg-background shadow-md rounded-full p-2 text-navy hover:bg-blueberry-lighter/30 transition-colors"
            :aria-label="t('reviews.next')"
            @click="next"
          >
            <ChevronRight class="w-5 h-5" />
          </button>

          <div class="flex justify-center gap-2 mt-6">
            <button
              v-for="(review, index) in reviews"
              :key="review.id"
              type="button"
              class="w-2.5 h-2.5 rounded-full transition-colors"
              :class="index === currentIndex ? 'bg-leaf' : 'bg-muted-foreground/30'"
              :aria-label="t('reviews.goTo', { index: index + 1 })"
              @click="goTo(index)"
            />
          </div>
        </template>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import { useReviews } from "@acme/api";
import { Card, CardContent } from "@acme/ui";
import { ChevronLeft, ChevronRight, Star, UserRound } from "lucide-vue-next";

const AUTOPLAY_INTERVAL_MS = 5000;

const { t, locale } = useI18n();
const reviewsResult = useReviews().getReviews(locale.value);

watch(locale, (lang) => {
  reviewsResult.refresh({ lang });
});

const reviews = computed(() =>
  reviewsResult.value.success ? reviewsResult.value.content.reviews : [],
);
const rating = computed(() =>
  reviewsResult.value.success ? reviewsResult.value.content.rating : null,
);
const userRatingCount = computed(() =>
  reviewsResult.value.success ? reviewsResult.value.content.user_rating_count : 0,
);
const formattedRating = computed(() =>
  rating.value !== null ? rating.value.toFixed(1) : "",
);

const currentIndex = ref(0);
let timer: ReturnType<typeof setInterval> | undefined;

function stop() {
  if (timer) {
    clearInterval(timer);
    timer = undefined;
  }
}

function start() {
  stop();
  if (reviews.value.length > 1) {
    timer = setInterval(next, AUTOPLAY_INTERVAL_MS);
  }
}

function next() {
  if (!reviews.value.length) return;
  currentIndex.value = (currentIndex.value + 1) % reviews.value.length;
}

function prev() {
  if (!reviews.value.length) return;
  currentIndex.value =
    (currentIndex.value - 1 + reviews.value.length) % reviews.value.length;
  start();
}

function goTo(index: number) {
  currentIndex.value = index;
  start();
}

function pause() {
  stop();
}

function resume() {
  start();
}

watch(
  () => reviews.value.length,
  (length) => {
    if (currentIndex.value >= length) {
      currentIndex.value = 0;
    }
    start();
  },
);

onMounted(start);
onBeforeUnmount(stop);
</script>
